/**
 * Order entry: category rail → product grid → modifier picker → cart.
 *
 * The layout is fixed three-column on a terminal and stacks on a tablet. The
 * product grid is the largest region because that is where 90% of the taps
 * land; the cart is always visible because a cashier reading an order back to a
 * guest must never have to navigate to see it.
 *
 * OFFLINE: sending an order while the network is down queues it in the outbox
 * (`ORDER_CREATE` is on the allow list) with a client UUID + idempotency key.
 * Everything else on this screen needs the server.
 */

import { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  Input,
  LoadingState,
  Modal,
  MoneyText,
  SearchField,
  Textarea,
  useToast,
} from '@sonauto/ui';
import { useApi } from '../../auth/AuthProvider';
import { useAsync, errorMessage } from '../../lib/hooks';
import { useOutbox } from '../../offline/OutboxProvider';
import { newIdempotencyKey } from '../../lib/api';
import { spiceLabel } from '../../lib/format';
import type { MenuCategory, Product, ProductDetailResponse, ProductVariant } from '../../lib/types';
import {
  addLine,
  cartEstimateMinor,
  cartItemCount,
  defaultSelection,
  groupRules,
  lineEstimateMinor,
  removeLine,
  setNote,
  setQuantity,
  setSpice,
  toCartModifier,
  toLineInputs,
  validateGroup,
  EMPTY_CART,
} from './cart';
import type { Cart, CartModifier } from './cart';

export interface OrderEntryProps {
  readonly branchId: string;
  readonly diningSessionId: string | null;
  readonly tableId: string | null;
  readonly tableLabel: string;
  /** Present when adding to an existing order ("gọi thêm món"). */
  readonly appendToOrderId: string | null;
  readonly onDone: () => void;
  readonly onCancel: () => void;
}

export function OrderEntry({
  branchId,
  diningSessionId,
  tableId,
  tableLabel,
  appendToOrderId,
  onDone,
  onCancel,
}: OrderEntryProps): React.JSX.Element {
  const api = useApi();
  const toast = useToast();
  const outbox = useOutbox();

  const [categoryId, setCategoryId] = useState<string>('');
  const [search, setSearch] = useState('');
  const [cart, setCart] = useState<Cart>(EMPTY_CART);
  const [picking, setPicking] = useState<Product | null>(null);
  const [orderNote, setOrderNote] = useState('');
  const [sending, setSending] = useState(false);

  const categories = useAsync(
    (signal) => api.menu.categories({ branchId, activeOnly: true }, { signal }),
    [branchId],
  );

  const products = useAsync(
    (signal) =>
      api.menu.products(
        {
          pageSize: 200,
          ...(categoryId === '' ? {} : { categoryId }),
          ...(search.trim() === '' ? {} : { search: search.trim() }),
          status: 'ACTIVE',
        },
        { signal },
      ),
    [categoryId, search],
  );

  const posCategories = useMemo<MenuCategory[]>(
    () => (categories.data?.categories ?? []).filter((c) => c.visibleToPos && c.isActive),
    [categories.data],
  );

  const send = async (): Promise<void> => {
    if (cart.lines.length === 0) return;
    setSending(true);
    const items = toLineInputs(cart);
    try {
      if (appendToOrderId !== null) {
        // NOTE: POST /orders/:id/items is NOT idempotency-protected server-side,
        // which is why the button is locked for the whole round trip.
        await api.orders.addItems(appendToOrderId, { items });
        toast.success('Đã thêm món vào đơn');
      } else {
        const created = await api.orders.create({
          branchId,
          diningSessionId,
          tableId,
          orderType: tableId === null ? 'TAKEAWAY' : 'DINE_IN',
          channel: 'POS',
          note: orderNote.trim() === '' ? null : orderNote.trim(),
          items,
        });
        toast.success(`Đã tạo đơn ${created.orderNo}`);
      }
      setCart(EMPTY_CART);
      onDone();
    } catch (cause: unknown) {
      // Offline (or the request never left): queue it. `ORDER_CREATE` is one of
      // the few operations the spec allows offline; "thêm món" into an existing
      // order is NOT, because the server prices the whole bill and a late replay
      // could land on an order that has since been paid.
      if (!outbox.snapshot.online && appendToOrderId === null) {
        const key = newIdempotencyKey();
        await outbox.queue({
          kind: 'ORDER_CREATE',
          idempotencyKey: key,
          label: `Đơn ${tableLabel} — ${cartItemCount(cart)} món`,
          branchId,
          request: {
            method: 'POST',
            path: '/v1/orders',
            body: {
              branchId,
              diningSessionId,
              tableId,
              orderType: tableId === null ? 'TAKEAWAY' : 'DINE_IN',
              channel: 'POS',
              note: orderNote.trim() === '' ? null : orderNote.trim(),
              clientUuid: key,
              items,
            },
          },
        });
        toast.warning('Đang ngoại tuyến', 'Đơn đã lưu vào hàng đợi và sẽ gửi khi có mạng.');
        setCart(EMPTY_CART);
        onDone();
      } else {
        toast.error('Không gửi được đơn', errorMessage(cause));
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="grid h-full grid-cols-1 lg:grid-cols-[13rem_1fr_22rem]">
      {/* --- category rail ------------------------------------------------ */}
      <nav className="hidden min-h-0 overflow-y-auto border-r border-line bg-surface p-2 lg:block">
        <Button
          fullWidth
          className="mb-1 justify-start"
          variant={categoryId === '' ? 'primary' : 'ghost'}
          onClick={() => setCategoryId('')}
        >
          Tất cả
        </Button>
        {categories.initialLoading ? <LoadingState compact label="Đang tải nhóm món…" /> : null}
        {posCategories.map((category) => (
          <Button
            key={category.id}
            fullWidth
            className="mb-1 justify-start"
            variant={categoryId === category.id ? 'primary' : 'ghost'}
            onClick={() => setCategoryId(category.id)}
          >
            <span className="text-flow text-left">{category.nameVi}</span>
          </Button>
        ))}
      </nav>

      {/* --- product grid -------------------------------------------------- */}
      <section className="flex min-h-0 flex-col">
        <div className="flex items-center gap-2 border-b border-line bg-surface p-2">
          <SearchField
            className="w-full"
            initialValue={search}
            onSearch={setSearch}
            placeholder="Tìm món theo tên hoặc mã…"
          />
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-3">
          {products.initialLoading ? <LoadingState label="Đang tải món…" /> : null}
          {products.error !== null && products.data === null ? (
            <ErrorState message={products.error.message} onRetry={products.reload} />
          ) : null}
          {products.data !== null && products.data.products.length === 0 ? (
            <EmptyState title="Không có món phù hợp" description="Đổi nhóm món hoặc xoá từ khoá tìm kiếm." />
          ) : null}
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-4">
            {(products.data?.products ?? []).map((product) => (
              <button
                key={product.id}
                type="button"
                onClick={() => setPicking(product)}
                className="tap-xl flex flex-col justify-between rounded-md bg-surface p-3 text-left shadow-card ring-1 ring-line transition hover:ring-brand-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <span className="text-flow text-sm font-semibold text-fg">{product.nameVi}</span>
                <span className="mt-2 flex items-center justify-between gap-2">
                  <MoneyText amountMinor={product.basePriceMinor} currency={product.currency} className="text-sm" />
                  {product.spiceLevel > 0 ? (
                    <Badge tone="danger" size="sm">
                      {spiceLabel(product.spiceLevel)}
                    </Badge>
                  ) : null}
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* --- cart ----------------------------------------------------------- */}
      <aside className="flex min-h-0 flex-col border-l border-line bg-surface">
        <header className="border-b border-line p-3">
          <h2 className="text-sm font-semibold text-fg">
            {appendToOrderId === null ? 'Đơn mới' : 'Gọi thêm món'} · {tableLabel}
          </h2>
          <p className="text-xs text-fg-subtle">{cartItemCount(cart)} món trong giỏ</p>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto p-2">
          {cart.lines.length === 0 ? (
            <EmptyState title="Chưa chọn món" description="Chạm vào món ở giữa màn hình để thêm." />
          ) : null}
          <ul className="space-y-2">
            {cart.lines.map((line) => (
              <li key={line.key} className="rounded-md bg-surface-alt p-2 ring-1 ring-line">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-flow text-sm font-semibold text-fg">
                      {line.productName}
                      {line.variantName === null ? '' : ` · ${line.variantName}`}
                    </p>
                    {line.modifiers.length > 0 ? (
                      <p className="text-flow text-xs text-fg-muted">
                        {line.modifiers.map((m) => `${m.name}${m.quantity > 1 ? ` ×${m.quantity}` : ''}`).join(', ')}
                      </p>
                    ) : null}
                    {line.spiceLevel !== null ? (
                      <p className="text-xs text-danger-fg">{spiceLabel(line.spiceLevel)}</p>
                    ) : null}
                  </div>
                  <MoneyText amountMinor={lineEstimateMinor(line)} className="text-sm font-semibold" />
                </div>

                <div className="mt-2 flex items-center gap-2">
                  <Button size="sm" variant="secondary" onClick={() => setCart(setQuantity(cart, line.key, line.quantity - 1))}>
                    −
                  </Button>
                  <span className="w-8 text-center text-sm font-semibold sa-tabular">{line.quantity}</span>
                  <Button size="sm" variant="secondary" onClick={() => setCart(setQuantity(cart, line.key, line.quantity + 1))}>
                    +
                  </Button>
                  <Button size="sm" variant="ghost" className="ml-auto" onClick={() => setCart(removeLine(cart, line.key))}>
                    Xoá
                  </Button>
                </div>

                <Input
                  className="mt-2"
                  placeholder="Ghi chú cho bếp…"
                  value={line.note ?? ''}
                  onChange={(e) => setCart(setNote(cart, line.key, e.target.value))}
                />
                <div className="mt-2 flex flex-wrap gap-1">
                  {[0, 1, 2, 3, 4, 5].map((level) => (
                    <Button
                      key={level}
                      size="sm"
                      variant={line.spiceLevel === level ? 'primary' : 'ghost'}
                      onClick={() => setCart(setSpice(cart, line.key, line.spiceLevel === level ? null : level))}
                    >
                      {spiceLabel(level)}
                    </Button>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        </div>

        <footer className="space-y-2 border-t border-line p-3">
          <Textarea
            rows={2}
            placeholder="Ghi chú cho cả đơn…"
            value={orderNote}
            onChange={(e) => setOrderNote(e.target.value)}
          />
          <div className="flex items-center justify-between text-sm">
            <span className="text-fg-muted">Tạm tính (ước lượng)</span>
            <MoneyText amountMinor={cartEstimateMinor(cart)} className="text-base font-bold" />
          </div>
          <p className="text-flow text-[11px] text-fg-subtle">
            Số tiền chính thức do máy chủ tính (thuế, phí phục vụ, giá theo khung giờ, làm tròn).
          </p>
          <div className="flex gap-2">
            <Button variant="secondary" fullWidth onClick={onCancel}>
              Huỷ
            </Button>
            <Button size="touch" fullWidth loading={sending} disabled={cart.lines.length === 0} onClick={() => void send()}>
              {appendToOrderId === null ? 'Tạo đơn' : 'Thêm vào đơn'}
            </Button>
          </div>
        </footer>
      </aside>

      {picking !== null ? (
        <ModifierPicker
          product={picking}
          onClose={() => setPicking(null)}
          onAdd={(input) => {
            setCart(addLine(cart, { ...input, product: picking }));
            setPicking(null);
          }}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------

interface ModifierPickerProps {
  readonly product: Product;
  readonly onClose: () => void;
  readonly onAdd: (input: {
    variant: ProductVariant | null;
    modifiers: readonly CartModifier[];
    quantity: number;
    note: string | null;
    spiceLevel: number | null;
  }) => void;
}

/**
 * The modifier sheet.
 *
 * Fetches `GET /v1/products/:id` because the list endpoint does not carry
 * variants, modifier groups or allergens — and a cashier picking "ít đá, 50%
 * đường, thêm trân châu" needs all three.
 */
function ModifierPicker({ product, onClose, onAdd }: ModifierPickerProps): React.JSX.Element {
  const api = useApi();
  const detail = useAsync<ProductDetailResponse>((signal) => api.menu.product(product.id, { signal }), [product.id]);

  const [variantId, setVariantId] = useState<string | null>(null);
  const [selected, setSelected] = useState<readonly CartModifier[] | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [note, setNoteText] = useState('');
  const [spice, setSpiceLevel] = useState<number | null>(null);

  const groups = detail.data?.modifierGroups ?? [];
  const variants = detail.data?.variants ?? [];
  const current = selected ?? (detail.data === null ? [] : defaultSelection(groups));
  const activeVariant = variants.find((v) => v.id === variantId) ?? variants.find((v) => v.isDefault) ?? null;

  const problems = groups.map((group) => validateGroup(group, current.filter((m) => m.modifierGroupId === group.id)));
  const firstProblem = problems.find((p): p is string => p !== null) ?? null;

  const toggle = (groupId: string, modifierId: string): void => {
    const group = groups.find((g) => g.id === groupId);
    if (group === undefined) return;
    const modifier = group.modifiers.find((m) => m.id === modifierId);
    if (modifier === undefined) return;
    const rules = groupRules(group);
    const without = current.filter((m) => m.modifierId !== modifierId);
    const wasSelected = without.length !== current.length;

    if (wasSelected) {
      setSelected(without);
      return;
    }
    // SINGLE groups replace, MULTIPLE groups append up to maxSelect.
    if (group.selectionType === 'SINGLE' || rules.maxSelect === 1) {
      setSelected([...current.filter((m) => m.modifierGroupId !== groupId), toCartModifier(group, modifier, 1)]);
      return;
    }
    setSelected([...current, toCartModifier(group, modifier, 1)]);
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={product.nameVi}
      description={product.shortDescriptionVi ?? undefined}
      size="lg"
      footer={
        <div className="flex w-full items-center gap-3">
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={() => setQuantity(Math.max(1, quantity - 1))}>
              −
            </Button>
            <span className="w-10 text-center text-lg font-bold sa-tabular">{quantity}</span>
            <Button variant="secondary" onClick={() => setQuantity(quantity + 1)}>
              +
            </Button>
          </div>
          <Button
            size="touch"
            className="ml-auto"
            disabled={firstProblem !== null}
            onClick={() =>
              onAdd({
                variant: activeVariant,
                modifiers: current,
                quantity,
                note: note === '' ? null : note,
                spiceLevel: spice,
              })
            }
          >
            Thêm vào giỏ
          </Button>
        </div>
      }
    >
      {detail.initialLoading ? <LoadingState label="Đang tải tuỳ chọn…" /> : null}
      {detail.error !== null ? <ErrorState message={detail.error.message} onRetry={detail.reload} /> : null}

      {detail.data !== null ? (
        <div className="space-y-4">
          {detail.data.allergens.length > 0 ? (
            <div className="rounded-md bg-danger-bg p-3 ring-1 ring-danger-solid/40">
              <p className="text-sm font-semibold text-danger-fg">Dị ứng</p>
              <p className="text-flow text-sm text-danger-fg">
                {detail.data.allergens
                  .map((a) => `${a.code}${a.confirmed ? '' : ' (chưa xác minh)'}`)
                  .join(', ')}
              </p>
            </div>
          ) : null}

          {variants.length > 0 ? (
            <section>
              <h3 className="text-sm font-semibold text-fg">Phiên bản</h3>
              <div className="mt-2 flex flex-wrap gap-2">
                {variants
                  .filter((v) => v.isActive)
                  .map((variant) => (
                    <Button
                      key={variant.id}
                      variant={(activeVariant?.id ?? '') === variant.id ? 'primary' : 'secondary'}
                      onClick={() => setVariantId(variant.id)}
                    >
                      {variant.nameVi}
                      {variant.priceDeltaMinor === 0 ? '' : ` (+${variant.priceDeltaMinor})`}
                    </Button>
                  ))}
              </div>
            </section>
          ) : null}

          {groups.map((group) => {
            const rules = groupRules(group);
            return (
              <section key={group.id}>
                <h3 className="text-sm font-semibold text-fg">
                  {group.nameVi}
                  {rules.required ? <span className="ml-1 text-danger-fg">*</span> : null}
                  <span className="ml-2 text-xs font-normal text-fg-subtle">
                    {group.groupKind} · chọn {rules.minSelect}–{rules.maxSelect}
                  </span>
                </h3>
                <div className="mt-2 flex flex-wrap gap-2">
                  {group.modifiers
                    .filter((m) => m.isActive)
                    .map((modifier) => {
                      const on = current.some((m) => m.modifierId === modifier.id);
                      return (
                        <Button
                          key={modifier.id}
                          variant={on ? 'primary' : 'secondary'}
                          onClick={() => toggle(group.id, modifier.id)}
                        >
                          {modifier.nameVi}
                          {modifier.priceDeltaMinor !== 0 ? (
                            <MoneyText amountMinor={modifier.priceDeltaMinor} className="ml-2 text-xs" />
                          ) : null}
                        </Button>
                      );
                    })}
                </div>
              </section>
            );
          })}

          <section>
            <h3 className="text-sm font-semibold text-fg">Mức cay</h3>
            <div className="mt-2 flex flex-wrap gap-2">
              {[0, 1, 2, 3, 4, 5].map((level) => (
                <Button
                  key={level}
                  variant={spice === level ? 'primary' : 'secondary'}
                  onClick={() => setSpiceLevel(spice === level ? null : level)}
                >
                  {spiceLabel(level)}
                </Button>
              ))}
            </div>
          </section>

          <section>
            <h3 className="text-sm font-semibold text-fg">Ghi chú</h3>
            <Textarea
              className="mt-2"
              rows={2}
              value={note}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Ví dụ: không hành, ít muối…"
            />
          </section>

          {firstProblem !== null ? <p className="text-sm text-danger-fg">{firstProblem}</p> : null}
        </div>
      ) : null}
    </Modal>
  );
}
